// src/components/Navigation.tsx - Updated with modular color system
'use client';

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import {
  KeyboardArrowDown,
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Phone as PhoneIcon,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Link from 'next/link';
import Image from 'next/image';
import { api, SiteSettings, getMediaUrl } from '@/lib/api';

import { withOpacity, darkTheme } from '@/lib/colors';
import { useColorMode, useColorTheme } from '@/components/ColorModeProvider';

// The top header tier (logo + utility icons + theme toggle) is intentionally
// pinned to the dark palette in every mode — the Dalhousie logo PNG has a
// white wordmark that requires a dark surface. The lower nav bar, dropdowns,
// and mobile drawer follow the active theme so they read as part of the page.
const navColors = darkTheme;

// LinkWrapper component for clean Link integration
interface LinkWrapperProps {
  href: string;
  children: React.ReactNode;
  onClick?: () => void;
  sx?: any;
}

const LinkWrapper: React.FC<LinkWrapperProps> = ({ href, children, onClick, sx }) => {
  return (
    <Box
      component={Link}
      href={href}
      onClick={onClick}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// HEADER TIER (always dark) — pinned to navColors
const HeaderAppBar = styled(AppBar)({
  backgroundColor: withOpacity(navColors.background.elevated, 0.95),
  backdropFilter: 'blur(10px)',
  color: navColors.text.primary,
  boxShadow: `0 2px 10px ${withOpacity(navColors.background.default, 0.3)}`,
});

const UtilityButton = styled(IconButton)({
  color: navColors.text.secondary,
  margin: '0 4px',
  '&:hover': {
    color: navColors.brand.accent.main,
    backgroundColor: withOpacity(navColors.brand.accent.main, 0.1),
  },
});

// NAV TIER (theme-aware) — follows the active light/dark mode

const NavigationBar = styled(Paper)(({ theme }) => {
  const colors = theme.colors;
  return {
    backgroundColor: withOpacity(colors.background.paper, 0.95),
    backdropFilter: 'blur(10px)',
    color: colors.text.primary,
    borderRadius: 0,
    boxShadow: `0 2px 8px ${withOpacity(colors.background.default, 0.3)}`,
    borderBottom: `1px solid ${colors.border.primary}`,
    position: 'fixed',
    top: '72px',
    left: 0,
    right: 0,
    zIndex: 1099,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  };
});

const NavButton = styled(Button)(({ theme }) => {
  const colors = theme.colors;
  return {
    color: colors.text.primary,
    fontWeight: 500,
    fontSize: '0.95rem',
    padding: '12px 20px',
    margin: '0 2px',
    borderRadius: 0,
    textTransform: 'none',
    position: 'relative',
    '&:hover': {
      backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
      color: colors.brand.accent.dark,
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '3px',
        backgroundColor: colors.brand.accent.main,
      },
    },
    '&.active': {
      color: colors.brand.accent.dark,
      backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '3px',
        backgroundColor: colors.brand.accent.main,
      },
    },
  };
});

const DropdownMenu = styled(Menu)(({ theme }) => {
  const colors = theme.colors;
  return {
    '& .MuiPaper-root': {
      backgroundColor: colors.background.paper,
      color: colors.text.primary,
      border: `1px solid ${colors.border.primary}`,
      borderRadius: 0,
      marginTop: 0,
      minWidth: 220,
      boxShadow: `0 8px 32px ${withOpacity(colors.text.primary, 0.15)}`,
    },
  };
});

const DropdownMenuItem = styled(MenuItem)(({ theme }) => {
  const colors = theme.colors;
  return {
    color: colors.text.primary,
    fontSize: '0.9rem',
    padding: '12px 20px',
    '&:hover': {
      backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
      color: colors.brand.accent.dark,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  };
});

const MobileDrawer = styled(Drawer)(({ theme }) => {
  const colors = theme.colors;
  return {
    '& .MuiDrawer-paper': {
      backgroundColor: colors.background.paper,
      color: colors.text.primary,
      width: 320,
      borderRight: `1px solid ${colors.border.primary}`,
    },
  };
});

interface NavigationProps {
  // Add any props needed for active page detection
}

const Navigation: React.FC<NavigationProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Header tier uses navColors (always dark); the rest of the nav (links,
  // dropdowns, mobile drawer) reads from the active theme so it matches
  // the page surface in light or dark mode.
  const headerColors = navColors;
  const colors = useColorTheme();
  const { mode, toggleMode } = useColorMode();
  
  // Desktop dropdown states
  const [peopleAnchor, setPeopleAnchor] = useState<null | HTMLElement>(null);
  const [governanceAnchor, setGovernanceAnchor] = useState<null | HTMLElement>(null);
  
  // Mobile drawer states
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(null);

  // Site settings state
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);

  // Fetch site settings on component mount
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const settings = await api.getSiteSettings();
        setSiteSettings(settings);
        
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
      } finally {
        setLogoLoading(false);
      }
    };

    fetchSiteSettings();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSubmenuToggle = (menu: string) => {
    setMobileSubmenuOpen(mobileSubmenuOpen === menu ? null : menu);
  };

    const navItems = [
        { label: 'Home', href: '/' },
        {
            label: 'People',
            href: '/people',
            dropdown: [
            { label: 'Program Director and Investigators', href: '/people/investigators' },
            { label: 'Students', href: '/people/students' },
            ],
        },
        {
            label: 'Governance',
            href: '/governance',
            dropdown: [
            { label: 'Industry Advisory Board', href: '/governance/advisory-board' },
            { label: 'Program Committee', href: '/governance/program-committee' },
            ],
        },
        { label: 'Research Themes', href: '/research-themes' },
        { label: 'News & Events', href: '/news-events' },
        { label: 'Publications', href: '/publications' },
        { label: 'Resources', href: '/resources' },
        { label: 'Contact', href: '/contact' },
    ];

  const MobileNavContent = () => (
    <Box sx={{ width: 320, height: '100%', pt: 2 }}>
      {/* Mobile Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        px: 3, 
        py: 2,
        mb: 2 
      }}>
        <Typography variant="h6" sx={{ color: colors.brand.accent.main, fontWeight: 600 }}>
          {siteSettings?.siteName || 'NSERC CREATE'}
        </Typography>
        <IconButton onClick={handleDrawerToggle} sx={{ color: colors.text.primary }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Mobile Navigation Items */}
      <List sx={{ px: 1 }}>
        {navItems.map((item) => (
          <Box key={item.label}>
            <ListItem disablePadding>
              {item.dropdown ? (
                <ListItemButton
                  onClick={() => handleSubmenuToggle(item.label)}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
                    },
                  }}
                >
                  <ListItemText 
                    primary={item.label} 
                    sx={{ 
                      color: colors.text.primary,
                      '& .MuiListItemText-primary': {
                        fontWeight: 500,
                        fontSize: '1rem',
                      }
                    }} 
                  />
                  {mobileSubmenuOpen === item.label ? 
                    <ExpandLess sx={{ color: colors.brand.accent.main }} /> : 
                    <ExpandMore sx={{ color: colors.text.primary }} />
                  }
                </ListItemButton>
              ) : (
                <LinkWrapper href={item.href} onClick={handleDrawerToggle} sx={{ width: '100%' }}>
                  <ListItemButton
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
                      },
                    }}
                  >
                    <ListItemText 
                      primary={item.label} 
                      sx={{ 
                        color: colors.text.primary,
                        '& .MuiListItemText-primary': {
                          fontWeight: 500,
                          fontSize: '1rem',
                        }
                      }} 
                    />
                  </ListItemButton>
                </LinkWrapper>
              )}
            </ListItem>
            
            {item.dropdown && (
              <Collapse in={mobileSubmenuOpen === item.label} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.dropdown.map((subItem) => (
                    <ListItem key={subItem.label} disablePadding>
                      <LinkWrapper href={subItem.href} onClick={handleDrawerToggle} sx={{ width: '100%' }}>
                        <ListItemButton
                          sx={{
                            pl: 4,
                            pr: 2,
                            py: 1,
                            borderRadius: 1,
                            mx: 1,
                            mb: 0.5,
                            '&:hover': {
                              backgroundColor: withOpacity(colors.brand.accent.main, 0.1),
                            },
                          }}
                        >
                          <ListItemText 
                            primary={subItem.label} 
                            sx={{ 
                              color: colors.text.secondary,
                              '& .MuiListItemText-primary': {
                                fontSize: '0.9rem',
                              }
                            }} 
                          />
                        </ListItemButton>
                      </LinkWrapper>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>

      {/* Mobile Contact Info */}
      <Box sx={{ 
        mt: 'auto',
        p: 3,
        borderTop: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}`,
        backgroundColor: withOpacity(colors.brand.accent.main, 0.05)
      }}>
        <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
          Contact Information
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.disabled, fontSize: '0.85rem' }}>
          Faculty of Computer Science<br />
          Dalhousie University<br />
          Halifax, NS B3H 4R2
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Spacer for fixed headers */}
      <Box sx={{ height: '116px' }} /> {/* 64px header + 52px nav bar */}
      
      {/* Top Header - Logo and Utilities */}
      <HeaderAppBar position="fixed" elevation={0}>
        <Container maxWidth="lg">
          <Toolbar sx={{ py: 1, minHeight: '64px' }}>
            {/* Logo */}
            <LinkWrapper href="/" sx={{ display: 'flex', alignItems: 'center', mr: 'auto' }}>
              {!logoLoading && siteSettings?.primaryLogo ? (
                <Box
                  sx={{
                    width: { xs: 140, sm: 180, md: 220 },
                    height: { xs: 40, sm: 50, md: 60 },
                    position: 'relative',
                    mr: 2,
                    borderRadius: 1,
                    padding: '4px',
                  }}
                >
                  <Image
                    src={getMediaUrl(siteSettings.primaryLogo)}
                    alt={siteSettings.siteName}
                    fill
                    style={{
                      objectFit: 'contain',
                    }}
                    sizes="(max-width: 600px) 140px, (max-width: 900px) 180px, 220px"
                    unoptimized={true} // Disable image optimization - png
                    priority
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: { xs: 140, sm: 180, md: 220 },
                    height: { xs: 40, sm: 50, md: 60 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: withOpacity(headerColors.brand.accent.main, 0.9),
                    border: `1px solid ${withOpacity(headerColors.brand.accent.main, 0.3)}`,
                    borderRadius: 1,
                    mr: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: headerColors.brand.accent.contrastText,
                      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                      fontWeight: 700
                    }}
                  >
                    {logoLoading ? 'Loading...' : 'DALHOUSIE UNIVERSITY'}
                  </Typography>
                </Box>
              )}

              {/* Program Title */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: headerColors.brand.accent.main,
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {siteSettings?.siteName || 'NSERC CREATE'}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: headerColors.text.secondary,
                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                    lineHeight: 1.2,
                    fontWeight: 500,
                  }}
                >
                  Cybersecurity Program
                </Typography>
              </Box>
            </LinkWrapper>

            {/* Utility Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Light/Dark mode toggle. Icon shows the mode you'll switch TO. */}
              <UtilityButton
                onClick={toggleMode}
                aria-label={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {mode === 'light' ? <DarkMode /> : <LightMode />}
              </UtilityButton>

              {/* Mobile Menu Button — sits in the dark header tier */}
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: headerColors.text.secondary,
                    ml: 1,
                    '&:hover': {
                      color: headerColors.brand.accent.main,
                      backgroundColor: withOpacity(headerColors.brand.accent.main, 0.1),
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </HeaderAppBar>

      {/* Navigation Menu Bar - Desktop Only */}
      <NavigationBar elevation={0}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {navItems.map((item) => (
              <Box key={item.label}>
                {item.dropdown ? (
                  <>
                    <NavButton
                      endIcon={<KeyboardArrowDown />}
                      onClick={(event) => {
                        if (item.label === 'People') {
                          setPeopleAnchor(event.currentTarget);
                        } else if (item.label === 'Governance') {
                          setGovernanceAnchor(event.currentTarget);
                        }
                      }}
                    >
                      {item.label}
                    </NavButton>
                    <DropdownMenu
                      anchorEl={item.label === 'People' ? peopleAnchor : governanceAnchor}
                      open={Boolean(item.label === 'People' ? peopleAnchor : governanceAnchor)}
                      onClose={() => {
                        if (item.label === 'People') {
                          setPeopleAnchor(null);
                        } else if (item.label === 'Governance') {
                          setGovernanceAnchor(null);
                        }
                      }}
                      MenuListProps={{
                        'aria-labelledby': 'basic-button',
                      }}
                    >
                      {item.dropdown.map((subItem) => (
                        <DropdownMenuItem
                          key={subItem.label}
                          onClick={() => {
                            if (item.label === 'People') {
                              setPeopleAnchor(null);
                            } else if (item.label === 'Governance') {
                              setGovernanceAnchor(null);
                            }
                          }}
                        >
                          <Link href={subItem.href} style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
                            {subItem.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenu>
                  </>
                ) : (
                  <LinkWrapper href={item.href}>
                    <NavButton>
                      {item.label}
                    </NavButton>
                  </LinkWrapper>
                )}
              </Box>
            ))}
          </Box>
        </Container>
      </NavigationBar>

      {/* Mobile Navigation Drawer */}
      <MobileDrawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <MobileNavContent />
      </MobileDrawer>
    </>
  );
};

export default Navigation;
